import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GetDetailsModel } from "../models/GetDetailsModel";
import ErrorsString from "../ErrorsString";
import {
  DetailsWoForBulkDownloadResponse,
  DownloadOptions,
  ImageInfo,
  ImageTypeDownloadable,
  ImagesInfo,
  ImagesLinkedToWo,
  Option,
  TemplateByCropGroup,
  buildDynamicDownloadUrl,
} from "../Utilities";
import CropTemplateByGroupConfJson from "../models/json/CropTemplateByGroupConf.json";
import {
  CropTemplateByGroup,
  CropTemplateByGroupConf,
  CropTemplateGroup,
} from "../models/ResponseModels/CropTemplateByGroupConf";
import Spinner from "../components/Spinner";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";

import TextField from "@mui/material/TextField";
import ScrollToTopButton from "../components/ScrollToTopButton";
import BulkDownloadImages from "../components/BulkDownloadImages";

import MassiveDownloadDropMenu from "../components/MassiveDownloadDropMenu";

export default function BulkDownloadPage() {
  const { uuid } = useParams();
  const [spinnerVisibility, setSpinnerVisibility] = useState(false);
  let [alertMessage, setAlertMessage] = useState<string>("");
  let [showAlert, setShowAlert] = useState<boolean>(false);

  const [detailsWo, setDetailsWo] =
    useState<DetailsWoForBulkDownloadResponse | null>(null);
  let [cropGroupSelected, setCropGroupSelected] = useState<Option | null>(null);
  let [cropGroupOptions, setCropGroupOptions] = useState<Option[]>([]);
  const [cropTemplateByGroupConf, setCropTemplateByGroupConf] =
    useState<CropTemplateByGroupConf>();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  let [exsistDownloadable, setExsistDownloadable] = useState<boolean>(false);
  let [exsistDownloadableByType, setExsistDownloadableByType] =
    useState<ImageTypeDownloadable>({});

  //recupero dettagli e immagini del work order
  const getDetails = useCallback(() => {
    window.scrollTo(0, 0);
    setSpinnerVisibility(true);

    console.log("Starting API call GetDetailsModel...");
    (async () => {
      const response = await GetDetailsModel(uuid);
      //console.log("GetDetailsModel response: " + JSON.stringify(response));

      const responseData = response?.data;
      const statusCode = response?.status;

      if (responseData === undefined || statusCode !== 200) {
        setError(ErrorsString.errorMessageGeneric);
      } else if (statusCode === 200) {
        setShowAlert(false);
        setAlertMessage("");
        setDetailsWo(responseData);
      }

      setSpinnerVisibility(false);
    })();
  }, [uuid]);

  function setError(message: string) {
    setShowAlert(true);
    setAlertMessage(message);
  }

  useEffect(() => {
    //leggo le conf per i crop template
    const bulkDownloadConf: CropTemplateByGroupConf = JSON.parse(
      JSON.stringify(CropTemplateByGroupConfJson),
    );
    setCropTemplateByGroupConf(bulkDownloadConf);
    setGroupList(bulkDownloadConf.cropTemplateGroups);
    (async () => {
      getDetails();
    })();
  }, [getDetails]);

  //setto i crop group selezionabili
  function setGroupList(cropTemplateGroups: CropTemplateGroup[]) {
    let groups: Option[] = [];
    for (let cropGroup of cropTemplateGroups) {
      groups.push({
        id: cropGroup.id,
        label: cropGroup.name,
      });
    }
    setCropGroupOptions(groups);
  }

  //setto i crop template disponibili su detailsWo in base al crop group selezionato
  function setCropTemplateToShow(cropGroupSelected: Option) {
    //definisco i crop template da mostrare in base a quello selezionato
    let cropTemplateGroupToShow: CropTemplateGroup = null;
    for (let cropTemplateGroup of cropTemplateByGroupConf.cropTemplateGroups) {
      if (cropTemplateGroup.name === cropGroupSelected.label) {
        cropTemplateGroupToShow = cropTemplateGroup;
      }
    }
    addCropTemplateAvailableOnImage(
      cropTemplateGroupToShow.cropTemplateByGroup,
      cropTemplateGroupToShow.name,
    );
  }

  //inserisco i crop template da mostrare per ogni image type (Primary key art...)
  function addCropTemplateAvailableOnImage(
    cropTemplateByGroup: CropTemplateByGroup[],
    groupName: string,
  ) {
    for (const label in detailsWo.imageLinkedToWo) {
      addCropTemplateAvailableOnImageByType(
        label,
        detailsWo.imageLinkedToWo,
        cropTemplateByGroup,
        groupName,
      );
    }

    for (const episodeName in detailsWo.episodeLinkedImages) {
      if (detailsWo.episodeLinkedImages.hasOwnProperty(episodeName)) {
        for (let labelType in detailsWo.episodeLinkedImages[episodeName]) {
          addCropTemplateAvailableOnImageByType(
            labelType,
            detailsWo.episodeLinkedImages[episodeName],
            cropTemplateByGroup,
            groupName,
            episodeName,
          );
        }
      }
    }
  }

  //@Codescene(disable-all)
  function addCropTemplateAvailableOnImageByType(
    label: string,
    images: any,
    cropTemplateByGroup: CropTemplateByGroup[],
    groupName: string,
    episodeName: string = "",
  ) {
    //episodeName e' stringa vuota se stiamo iterando le immagini linkata al WO (non quelle episodiche)
    const typeLabel = episodeName !== "" ? episodeName + "-" + label : label;

    if (!images.hasOwnProperty(label)) {
      return;
    }

    const imagesList = images[label];

    // Verifica che imagesList sia un array valido
    if (!Array.isArray(imagesList)) {
      console.warn(`images[${label}] is not an array:`, imagesList);
      return;
    }

    for (let imageInfo of imagesList) {
      if (imageInfo.isDownloadable) {
        setExsistDownloadable(true);
        setExsistDownloadableByType((prev) => ({
          ...prev,
          [typeLabel]: true,
        }));
        //svuoto availableTemplatesByCropGroup
        imageInfo.availableTemplatesByCropGroup = [];

        for (let cropTemplateGroup of cropTemplateByGroup) {
          //creo la variabile con le info del template da aggiungere
          let availableTemplateToPush: TemplateByCropGroup = {
            aspectRatio: cropTemplateGroup.aspectRatio,
            previewUrl: null,
            nameTemplate: cropTemplateGroup.name,
            label: cropTemplateGroup.label,
            resolution: cropTemplateGroup.resolution,
            downlodable: false,
            selectedForDownload: false,
          };

          //controllo se il template è disponibile sull'immagine (downlodable a true)
          availableTemplateToPush = setDownlodableOnTemplate(
            availableTemplateToPush,
            imageInfo,
            label,
            groupName,
          );

          imageInfo.availableTemplatesByCropGroup.push(availableTemplateToPush);
        }
      }
    }
  }

  //se il crop template da aggiungere è presente fra i saved template metto downlodable a true
  function setDownlodableOnTemplate(
    availableTemplateToPush: TemplateByCropGroup,
    imageInfo: any,
    imageType: string,
    groupName: string,
  ) {
    const imagesTemplate: ImageInfo[] = imageInfo.image_info;
    const isGreenStar: boolean = imageInfo.isGreenStar;

    for (let imageTemplate of imagesTemplate) {
      //se il template esiste tra i saved template metto downlodable a true
      const isDownlodable = checkIftemplateIsDownlodable(
        imageTemplate,
        availableTemplateToPush,
      );

      if (isDownlodable) {
        availableTemplateToPush.downlodable = true;

        availableTemplateToPush.previewUrl = imageTemplate.previewUrl
          ? imageTemplate.previewUrl
          : null;

        setSelectionOnTemplate(
          availableTemplateToPush,
          isGreenStar,
          imageType,
          groupName,
        );
      }
    }

    return availableTemplateToPush;
  }

  function checkIftemplateIsDownlodable(
    imageTemplate: ImageInfo,
    availableTemplateToPush: TemplateByCropGroup,
  ) {
    let isDownlodable = false;
    if (imageTemplate.aspectRatio === availableTemplateToPush.aspectRatio) {
      let savedTemplate = imageTemplate.savedTemplates.find(
        (template) =>
          template.name === availableTemplateToPush.nameTemplate &&
          template.resolution === availableTemplateToPush.resolution,
      );

      if (savedTemplate) {
        //image has a template (same aspect ratio, resolution and name) of the configuration for the selected group
        isDownlodable = true;
      }
    }

    return isDownlodable;
  }

  function setSelectionOnTemplate(
    availableTemplateToPush: TemplateByCropGroup,
    isGreenStar: boolean,
    imageType: string,
    groupName: string,
  ) {
    const isSelectedForDownload = isTemplateSelectedByDefault(
      isGreenStar,
      availableTemplateToPush.nameTemplate,
      imageType,
      groupName,
    );

    availableTemplateToPush.selectedForDownload = isSelectedForDownload;
  }

  function isTemplateSelectedByDefault(
    isGreenStar,
    nameTemplate,
    imageType,
    groupName,
  ) {
    let isSelectedForDownload = false;

    // if has green star and type is not Episodic then select by default, while if type is episodic select only if templatename is not Show/Series Level Key Art
    const groupsToCheck = ["Catchup Up TV", "Box Sets/Archive"];

    const isEpisodic = imageType === "Episodic";
    const isToExclude = isEpisodic && groupsToCheck.includes(groupName);

    if (isGreenStar && isToExclude) {
      const excludedToDefaultTemplates = [
        "Show Level Key Art",
        "Series Level Key Art",
      ];
      const isExcluedTemplate = excludedToDefaultTemplates.some(
        (excludedToDefaultTemplates) =>
          nameTemplate.includes(excludedToDefaultTemplates),
      );
      if (!isExcluedTemplate) {
        isSelectedForDownload = true;
      }
    } else if (isGreenStar) {
      isSelectedForDownload = true;
    }

    return isSelectedForDownload;
  }

  function combineImagesLinkedToWo(): ImagesLinkedToWo {
    const combined: ImagesLinkedToWo = {};

    // Copia imageLinkedToWo assicurandosi che tutti i valori siano array
    if (detailsWo.imageLinkedToWo) {
      Object.entries(detailsWo.imageLinkedToWo).forEach(([key, value]) => {
        combined[key] = Array.isArray(value) ? [...value] : [];
      });
    }

    // Itera su ogni elemento di episodeLinkedImages
    Object.values(detailsWo.episodeLinkedImages ?? {}).forEach(
      (imagesLinkedToWo) => {
        Object.entries(imagesLinkedToWo).forEach(([key, imagesArray]) => {
          // Assicurati che imagesArray sia un array
          if (!Array.isArray(imagesArray)) {
            console.warn(
              `episodeLinkedImages value for key ${key} is not an array:`,
              imagesArray,
            );
            return;
          }

          // Se la chiave esiste già e il valore è un array, unisce i valori
          if (combined[key] && Array.isArray(combined[key])) {
            combined[key] = [...combined[key], ...imagesArray];
          } else {
            combined[key] = [...imagesArray];
          }
        });
      },
    );

    return combined;
  }

  async function _getDynamicUrls(
    imageInfo: ImagesInfo,
    dynamicDownloadUrls: { [key: string]: DownloadOptions },
  ) {
    for (let cropTemplate of imageInfo.availableTemplatesByCropGroup) {
      if (cropTemplate.selectedForDownload) {
        let downloadOptions: DownloadOptions = {
          resolution: cropTemplate.resolution,
          ratio: cropTemplate.aspectRatio,
          previewUrl: cropTemplate.previewUrl,
          template: cropTemplate.nameTemplate,
          uuid: imageInfo.uuid,
          fileName: imageInfo.fileName,
        };

        const dynamicDownloadUrl = await buildDynamicDownloadUrl(
          downloadOptions,
          null,
        );

        dynamicDownloadUrls[dynamicDownloadUrl] = downloadOptions;
      }
    }

    return dynamicDownloadUrls;
  }

  async function getDynamicUrlsByImageType(
    imagesArray: ImagesLinkedToWo,
    imageType: string,
    dynamicDownloadUrls: { [key: string]: DownloadOptions },
  ) {
    const images = imagesArray[imageType];

    // Verifica che images sia un array valido
    if (!Array.isArray(images)) {
      console.warn(`imagesArray[${imageType}] is not an array:`, images);
      return dynamicDownloadUrls;
    }

    for (let imageInfo of images) {
      if (imageInfo.availableTemplatesByCropGroup) {
        dynamicDownloadUrls = await _getDynamicUrls(
          imageInfo,
          dynamicDownloadUrls,
        );
      }
    }

    return dynamicDownloadUrls;
  }

  async function getSelectedImagesDynamicUrls() {
    let dynamicDownloadUrls: { [key: string]: DownloadOptions } = {};

    const imagesArray: ImagesLinkedToWo = combineImagesLinkedToWo();

    for (const label in imagesArray) {
      if (imagesArray.hasOwnProperty(label)) {
        //itero i crop template per recuperare quelli selezionati
        dynamicDownloadUrls = await getDynamicUrlsByImageType(
          imagesArray,
          label,
          dynamicDownloadUrls,
        );
      }
    }
    return dynamicDownloadUrls;
  }

  return (
    <>
      <Spinner visibility={spinnerVisibility} />
      {showAlert ? <Alert severity="error">{alertMessage}</Alert> : <></>}
      <ScrollToTopButton></ScrollToTopButton>
      {detailsWo !== null ? (
        <div className="p-2">
          <div className="my-6">
            <div className="font-skyMedium">
              <span className="text-xl md:text-3xl">{detailsWo.title}</span>
              <span className="ml-4 text-lg text-slate-500">channel: </span>
              <span className=" text-xl md:text-3xl">{detailsWo.channel}</span>
            </div>
            <div className="flex flex-wrap justify-between items-center my-4 ">
              <div className="w-full md:w-1/3">
                <Autocomplete
                  disablePortal
                  disableClearable
                  id="cropGroups"
                  value={cropGroupSelected}
                  options={cropGroupOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Crop Group"
                      required
                      error={false}
                      helperText={""}
                    />
                  )}
                  onChange={(event: any, newCropGroupSelected: Option) => {
                    setCropGroupSelected(newCropGroupSelected);
                    setCropTemplateToShow(newCropGroupSelected);
                  }}
                />
              </div>
              <div className="w-full md:w-1/3 p-2 text-center">
                <div id="actions">
                  <MassiveDownloadDropMenu
                    getDownloadImagesMap={getSelectedImagesDynamicUrls}
                    loading={loading}
                    setLoading={setLoading}
                    success={success}
                    setSuccess={setSuccess}
                    error={false}
                    setError={null}
                    isButtonDisabled={
                      loading ||
                      cropGroupSelected == null ||
                      !exsistDownloadable
                    }
                    setShowAlert={setShowAlert}
                    setAlertMessage={setAlertMessage}
                    workOrderTitle={detailsWo.title}
                  />
                </div>
              </div>
              <div className="w-0 md:w-1/3"></div>
            </div>
          </div>
          <div className="my-4">
            <BulkDownloadImages
              detailsWo={detailsWo}
              cropGroupSelected={cropGroupSelected?.label || ""}
              exsistDownloadableByType={exsistDownloadableByType}
            ></BulkDownloadImages>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
